import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UIRootState } from '../../store/reducers';
import { 
  setDesignTab
} from '../../store/actions/gameUIActions';

interface ImageWithPlaceholderProps {
  baseSrc: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

const ImageWithPlaceholder: React.FC<ImageWithPlaceholderProps> = ({
  baseSrc,
  className = '',
  style = {},
  onClick
}) => {
  const [loading, setLoading] = useState(false);

  const imgRef = useRef<HTMLImageElement>(null);

  const handleLoad = () => {
    setLoading(false);
  };

  return (
    <div style={{ position: 'relative', ...style }} onClick={onClick}>
      {loading && (
        <div 
          className="placeholder placeholder-wave"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: style.borderRadius || '8px',
            zIndex: 1
          }}
        />
      )}
      <picture>
        <source srcSet={`${baseSrc}.webp`} type="image/webp" />
        <img
          ref={imgRef}
          src={`${baseSrc}.png`}
          className={className}
          loading="lazy"
          decoding="async"
          onLoad={handleLoad}
          onError={() => setLoading(false)}
          style={{
            ...style,
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.2s ease'
          }}
        />
      </picture>
    </div>
  );
};

export const DesignModal: React.FC = () => {
  const dispatch = useDispatch();
  const { designModal } = useSelector((state: UIRootState) => state.gameUI.modals);
  const design = useSelector((state: UIRootState) => state.gameUI.design);
  const labels = useSelector((state: UIRootState) => state.gameUI.labels);
  const buttons = useSelector((state: UIRootState) => state.gameUI.buttons);

  // Автоматически переключаемся на 'cards' если первая вкладка скрыта 
  React.useEffect(() => {
    if (buttons.hideDesignFirstTab && designModal.activeTab === 'patterns') {
      console.log('🎨 Auto-switching from patterns to cards for FreeCell');
      dispatch(setDesignTab('cards'));
    }
  }, [buttons.hideDesignFirstTab, designModal.activeTab, dispatch]);

  // Обновляем позицию индикатора при изменении activeTab
  React.useEffect(() => {
    const indicator = document.querySelector('.tab-indicator') as HTMLElement;
    if (indicator) {
      const tabs = buttons.hideDesignFirstTab ? ['cards', 'backgrounds'] : ['patterns', 'cards', 'backgrounds'];
      const index = tabs.indexOf(designModal.activeTab);
      if (index !== -1) {
        indicator.style.transform = `translateX(${index * 100}%)`;
        console.log('🎨 useEffect updating indicator - activeTab:', designModal.activeTab, 'index:', index);
      }
    }
  }, [designModal.activeTab, buttons.hideDesignFirstTab]);

  // Данные карт спины из tmp файлов
  const backs = [
    '1_blue', '6_orange', '3_red', '71_classic_blue', '72_classic_purple', '73_classic_red',
    '75_panda_leaves', '76_palace', '27_owl', '15_island', '53_cartoon_hat_cat', '45_astrology',
    '66_spider', '22_sakura', '7_ginger_kitten', '11_cat_glasses', '59_golden_crown', '44_chinese_wall',
    '46_sky', '4_red_pattern', '39_palace', '74_panda', '24_butterfly', '56_porsche', '35_pagoda',
    '2_blue_dark', '51_retro_car', '14_white_puppy', '41_white_dome', '10_cat_crown', '64_snowflakes',
    '62_gift', '63_winter_dog', '65_golden_bird', '25_ladybug', '30_bamboo_bear', '67_heart_flowers',
    '68_heart_cat', '70_heart_dog', '69_heart_glasses'
  ];

  // Данные фронтов карт из tmp файлов
  const fronts = [
    '13_classic', '11_white_large', '1_white', '2_ivory', '3_medieval',
    '12_classic_large', '4_gothic', '5_cats', '6_dogs', '7_elegant',
    '8_neon', '9_retro', '10_cartoonish'
  ];

  // Данные фонов из tmp файлов
  const backgrounds = [
    '1_green', '2_blue', '3_red', '4_orange',
    '39_field', '40_mountains_lake', '41_night_sky', '43_sakura', '42_national_park', '44_bridge',
    '11_poppies', '12_green_field', '13_waterfall', '14_beach', '15_turquoise_skies', '20_wood',
    '30_kittens', '31_mountains', '33_winter_night', '34_christmas_tree', '5_olive', '6_turquoise',
    '7_burgundy', '8_dark_purple'
  ];

  const handleButtonClick = (buttonKey: string, data?: any) => {
    console.log('🎨 DesignModal handleButtonClick:', buttonKey, data);
    document.dispatchEvent(new CustomEvent('ui-button-click', {
      detail: { buttonKey, data }
    }));
  };

  const handleDesignChange = (type: string, value: string) => {
    document.dispatchEvent(new CustomEvent('ui-design-change', { 
      detail: { type, value } 
    }));
  };

  const handleTabClick = (tab: string) => {
    document.dispatchEvent(new CustomEvent('ui-modal-action', {
      detail: { action: 'change-tab', tab }
    }));
    
    // Анимация индикатора как в tmp - учитываем скрытие первой вкладки
    const indicator = document.querySelector('.tab-indicator') as HTMLElement;
    if (indicator) {
      const tabs = buttons.hideDesignFirstTab ? ['cards', 'backgrounds'] : ['patterns', 'cards', 'backgrounds'];
      const index = tabs.indexOf(tab);
      indicator.style.transform = `translateX(${index * 100}%)`;
      console.log('🎨 handleTabClick - tab:', tab, 'tabs:', tabs, 'index:', index, 'transform:', `translateX(${index * 100}%)`);
    }

    // Скролл в начало контента
    const designContent = document.querySelector('.design-content') as HTMLElement;
    if (designContent) {
      designContent.scrollTop = 0;
    }
  };

  const getTabIndicatorPosition = () => {
    // Если первая вкладка скрыта, tabs массив будет только ['cards', 'backgrounds']
    const tabs = buttons.hideDesignFirstTab ? ['cards', 'backgrounds'] : ['patterns', 'cards', 'backgrounds'];
    const activeIndex = tabs.indexOf(designModal.activeTab);
    return `translateX(${activeIndex * 100}%)`;
  };

  return (
    <div 
      id="game-ui-design" 
      className="text-white flex-column h-100 w-100"
      style={{
        background: 'rgb(31, 31, 31, 0.3)',
        position: 'absolute',
        zIndex: -1,
        top: 0,
        display: 'none',
        opacity: 0,
      }}
    >
      <div className="design-content-wrapper">
        {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4" style={{ paddingTop: '20px' }}>
        <h5 id="design-header" className="mb-0 text-white" style={{ fontWeight: 600, fontSize: '22px' }}>{labels.designText}</h5>
        <button 
          id="close-design-button"
          className="btn btn-link text-white p-0 border-0"
          style={{ opacity: 0.6 }}
          onClick={() => handleButtonClick('close-design')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="design-tabs-container">
        <div className="design-tabs d-flex rounded-pill p-1 position-relative" style={{ background: '#6c757d', height: '42px' }}>
          <div 
            className="tab-indicator" 
            style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              width: buttons.hideDesignFirstTab ? 'calc(50% - 4px)' : 'calc(33.333% - 2.5px)',
              height: 'calc(100% - 8px)',
              background: '#28a745',
              borderRadius: '50px',
              transition: 'transform 0.3s ease',
              zIndex: 0,
              transform: getTabIndicatorPosition()
            }}
          />
          
          {/* Первая вкладка (Patterns) - скрывается для FreeCell */}
          {!buttons.hideDesignFirstTab && (
            <button 
              className={`design-tab ${designModal.activeTab === 'patterns' ? 'active' : ''}`}
              onClick={() => handleTabClick('patterns')}
            style={{
              flex: 1,
              border: 'none',
              borderRadius: '50px',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="25" viewBox="0 0 17 25" fill="none">
              <path d="M11.8217 1.89394L12.73 3.23614L13.6384 1.89394H11.8217Z" fill="white"/>
              <path d="M12.5054 3.56802L11.3724 1.89394H9.86847L8.73415 3.56999L10.6191 6.35518L12.5054 3.56802Z" fill="white"/>
              <path d="M10.8437 6.68706L12.73 3.8999L14.6134 6.68275L12.7271 9.46992L10.8437 6.68706Z" fill="white"/>
              <path d="M12.9517 9.8018L14.838 7.01463L15.1522 7.47879V12.1216L14.837 12.5874L12.9517 9.8018Z" fill="white"/>
              <path d="M14.6123 12.9192L12.7271 10.1337L10.8416 12.9197L12.7268 15.7053L14.6123 12.9192Z" fill="white"/>
              <path d="M12.9514 16.0372L14.837 13.2511L15.1522 13.7169V18.3578L14.8371 18.8234L12.9514 16.0372Z" fill="white"/>
              <path d="M15.0617 19.1552L15.1522 19.0215V19.2889L15.0617 19.1552Z" fill="white"/>
              <path d="M12.7268 16.369L14.6125 19.1552L12.7293 21.9377L10.8437 19.1515L12.7268 16.369Z" fill="white"/>
              <path d="M14.8371 19.4871L12.9539 22.2696L13.5201 23.1061H14.0435C14.6558 23.1061 15.1522 22.5973 15.1522 21.9697V19.9527L14.8371 19.4871Z" fill="white"/>
              <path d="M10.6191 19.4834L12.5047 22.2696L11.9386 23.1061H9.29925L8.73327 22.2698L10.6191 19.4834Z" fill="white"/>
              <path d="M12.7293 22.6014L12.3878 23.1061H13.0709L12.7293 22.6014Z" fill="white"/>
              <path d="M12.5022 16.0372L10.617 13.2516L8.73385 16.034L10.6191 18.8196L12.5022 16.0372Z" fill="white"/>
              <path d="M10.3945 19.1515L8.50924 16.3659L6.62344 19.1523L8.50866 21.9379L10.3945 19.1515Z" fill="white"/>
              <path d="M8.28405 22.2698L6.39883 19.4842L4.51317 22.2704L5.07872 23.1061H7.71807L8.28405 22.2698Z" fill="white"/>
              <path d="M8.16729 23.1061L8.50866 22.6017L8.85003 23.1061H8.16729Z" fill="white"/>
              <path d="M15.0616 12.9192L15.1522 12.7854V13.0531L15.0616 12.9192Z" fill="white"/>
              <path d="M10.6191 7.01894L12.5025 9.8018L10.617 12.5878L8.73358 9.80498L10.6191 7.01894Z" fill="white"/>
              <path d="M8.50897 10.1369L10.3924 12.9197L8.50924 15.7022L6.62585 12.9193L8.50897 10.1369Z" fill="white"/>
              <path d="M6.40124 13.2512L8.28463 16.034L6.39883 18.8205L4.51544 16.0376L6.40124 13.2512Z" fill="white"/>
              <path d="M4.29083 16.3695L6.17422 19.1523L4.28856 21.9385L2.40518 19.1557L4.29083 16.3695Z" fill="white"/>
              <path d="M2.18056 19.4876L4.06395 22.2704L3.49841 23.1061H2.95652C2.34421 23.1061 1.84783 22.5973 1.84783 21.9697V19.9792L2.18056 19.4876Z" fill="white"/>
              <path d="M4.28856 22.6023L3.94763 23.1061H4.6295L4.28856 22.6023Z" fill="white"/>
              <path d="M15.1522 6.55048L15.0626 6.68275L15.1522 6.81503V6.55048Z" fill="white"/>
              <path d="M14.838 6.35087L15.1522 5.88672V3.0303C15.1522 2.41767 14.6792 1.91826 14.0871 1.8948L12.9547 3.56802L14.838 6.35087Z" fill="white"/>
              <path d="M10.3945 6.68706L8.50954 3.90187L6.624 6.6879L8.50897 9.4731L10.3945 6.68706Z" fill="white"/>
              <path d="M8.28436 9.80498L6.39938 7.01978L4.51627 9.80223L6.40124 12.5874L8.28436 9.80498Z" fill="white"/>
              <path d="M6.17663 12.9193L4.29166 10.1341L2.40585 12.9205L4.29083 15.7057L6.17663 12.9193Z" fill="white"/>
              <path d="M4.06622 16.0376L2.18124 13.2524L1.84783 13.7451V18.3322L2.18056 18.8238L4.06622 16.0376Z" fill="white"/>
              <path d="M1.95595 19.1557L1.84783 18.9959V19.3155L1.95595 19.1557Z" fill="white"/>
              <path d="M8.50954 3.23811L7.59983 1.89394H9.41925L8.50954 3.23811Z" fill="white"/>
              <path d="M7.15061 1.89394L8.28493 3.56999L6.39938 6.35602L4.51241 3.56787L5.64529 1.89394H7.15061Z" fill="white"/>
              <path d="M4.2878 3.89975L6.17477 6.6879L4.29166 9.47035L2.40468 6.6822L4.2878 3.89975Z" fill="white"/>
              <path d="M2.18007 7.01408L4.06705 9.80223L2.18124 12.5887L1.84783 12.096V7.50499L2.18007 7.01408Z" fill="white"/>
              <path d="M1.84783 12.7598L1.95663 12.9205L1.84783 13.0813V12.7598Z" fill="white"/>
              <path d="M3.37952 1.89394L4.2878 3.23599L5.19607 1.89394H3.37952Z" fill="white"/>
              <path d="M4.06319 3.56787L2.93051 1.89425C2.33021 1.90841 1.84783 2.41162 1.84783 3.0303V5.8594L2.18007 6.35032L4.06319 3.56787Z" fill="white"/>
              <path d="M1.95546 6.6822L1.84783 6.52317V6.84123L1.95546 6.6822Z" fill="white"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M2.95652 0C1.32368 0 0 1.35671 0 3.0303V21.9697C0 23.6433 1.32368 25 2.95652 25H14.0435C15.6763 25 17 23.6433 17 21.9697V3.0303C17 1.35671 15.6763 0 14.0435 0H2.95652ZM15.5217 3.0303V18.4632L15.5275 18.4669L15.5217 18.4755V21.9697C15.5217 22.8065 14.8599 23.4848 14.0435 23.4848H2.95652C2.1401 23.4848 1.47826 22.8065 1.47826 21.9697V3.0303C1.47826 2.19351 2.1401 1.51515 2.95652 1.51515H14.0435C14.8599 1.51515 15.5217 2.19351 15.5217 3.0303Z" fill="white"/>
            </svg>
            </button>
          )}
          
          <button 
            className={`design-tab ${designModal.activeTab === 'cards' ? 'active' : ''}`}
            onClick={() => handleTabClick('cards')}
            style={{
              flex: 1,
              border: 'none',
              borderRadius: '50px',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M1.47207 9.39207C1.21135 7.89838 2.17259 6.47863 3.61905 6.22098L13.4405 4.47152C14.8869 4.21387 16.2709 5.21588 16.5316 6.70957L19.482 23.6131C19.7427 25.1068 18.7815 26.5266 17.3351 26.7842L7.51363 28.5337C6.06717 28.7913 4.68323 27.7893 4.42251 26.2956L1.47207 9.39207ZM6.25231 12.5403C6.42643 12.4146 6.69733 12.246 6.85518 12.1706C7.04063 12.0859 7.2904 12.0113 7.54858 11.961C7.81963 11.9128 8.05864 11.896 8.27037 11.9142C8.44352 11.9263 8.71513 11.9769 8.87387 12.026C9.05502 12.084 9.27541 12.1996 9.78285 12.5736L9.99776 12.2515C10.133 12.0468 10.301 11.8621 10.4516 11.745C10.5841 11.6439 10.8223 11.5026 10.9808 11.4314C11.1622 11.3475 11.412 11.2729 11.6547 11.2296C11.9056 11.185 12.1574 11.1702 12.3773 11.1869C12.5633 11.2011 12.883 11.2646 13.0892 11.3224C13.3842 11.4118 13.5448 11.495 13.8503 11.7158C14.1605 11.9401 14.2816 12.0604 14.4647 12.3202C14.5879 12.5004 14.7407 12.7785 14.8062 12.9388C14.8802 13.1235 14.959 13.4319 15.0238 13.8031C15.0995 14.2368 15.1186 14.4657 15.1005 14.7441C15.0902 14.9481 15.0389 15.2754 14.9859 15.4741C14.9371 15.672 14.8302 15.992 14.7549 16.1817C14.6637 16.4001 14.4388 16.7842 14.1428 17.2153C13.8881 17.5961 13.5192 18.0876 13.3287 18.3107C13.1382 18.5339 12.8004 18.9165 12.5798 19.158C12.3591 19.3994 11.9702 19.7998 11.7138 20.0519C11.4568 20.2998 11.2101 20.5114 11.1575 20.5208C11.1089 20.5294 10.8095 20.4151 10.4921 20.2695C10.1707 20.1246 9.6035 19.8386 9.22259 19.6399C8.84572 19.4404 8.30066 19.1375 8.01935 18.9597C7.73876 18.7861 7.30325 18.4895 7.05357 18.3018C6.8039 18.1141 6.48343 17.8314 6.34031 17.6806C6.20124 17.5291 6.00351 17.2805 5.90303 17.135C5.80255 16.9895 5.65741 16.7315 5.57902 16.5691C5.46802 16.3395 5.40517 16.1228 5.30236 15.6294C5.1879 15.0692 5.17343 14.9385 5.19278 14.5953C5.20089 14.3789 5.25843 14.0633 5.31095 13.8862C5.37407 13.6743 5.4952 13.4363 5.67435 13.1721C5.88262 12.8598 6.00529 12.7262 6.25231 12.5403Z" fill="white"/>
              <g filter="url(#filter0_d_12722_4458)">
              <path fillRule="evenodd" clipRule="evenodd" d="M17.4654 6.70457C17.7261 5.21088 19.1101 4.20887 20.5565 4.46652L30.378 6.21598C31.8244 6.47363 32.7857 7.89338 32.5249 9.38707L29.5745 26.2906C29.3138 27.7843 27.9298 28.7863 26.4834 28.5287L16.662 26.7792C15.2155 26.5216 14.2543 25.1018 14.515 23.6081L17.4654 6.70457ZM22.3091 12.5111C22.6368 12.3405 23.2175 12.0623 23.6049 11.8903C23.9885 11.7176 24.3338 11.5823 24.3723 11.5892C24.407 11.5953 24.5481 11.6968 24.6781 11.8164C24.8089 11.932 25.1242 12.2413 25.3732 12.4985C25.6228 12.7519 25.971 13.1232 26.148 13.3154C26.3242 13.5115 26.6511 13.8911 26.8697 14.163C27.0922 14.4356 27.3849 14.8292 27.5224 15.0425C27.6599 15.2558 27.8357 15.5683 27.9177 15.7356C27.9959 15.9021 28.113 16.2323 28.1796 16.4651C28.2423 16.6972 28.3091 17.0425 28.3272 17.2345C28.3507 17.532 28.3417 17.675 28.2458 18.2243C28.1608 18.7113 28.1035 18.926 28.0201 19.108C27.9609 19.2421 27.8048 19.4995 27.6738 19.681C27.4953 19.9304 27.3654 20.0599 27.1298 20.2268C26.9621 20.3456 26.7106 20.4896 26.5699 20.5448C26.4292 20.6001 26.1907 20.6701 26.0348 20.6986C25.8828 20.7277 25.5925 20.7524 25.3938 20.7531C25.1437 20.7527 24.926 20.726 24.7207 20.6734C24.5533 20.6315 24.2957 20.5374 24.1491 20.4671C24.0064 20.3975 23.8222 20.2924 23.7449 20.2345C23.6677 20.1765 23.5961 20.1316 23.587 20.138C23.5748 20.1399 23.5931 20.4444 23.6237 20.8154C23.6581 21.1871 23.6807 21.5125 23.676 21.5397C23.6698 21.5748 23.4367 21.5453 22.6471 21.4047C21.8499 21.2627 21.6324 21.2119 21.65 21.1789C21.6547 21.1691 21.6758 21.1231 21.7082 21.0528C21.7618 20.936 21.8463 20.752 21.9376 20.5552C22.0801 20.2392 22.205 19.9561 22.2091 19.9328C22.2152 19.8977 22.147 19.9016 22.0006 19.9438C21.8801 19.9746 21.6714 20.0097 21.5389 20.0182C21.4057 20.0307 21.1562 20.0264 20.9847 20.0079C20.774 19.9864 20.5604 19.9363 20.3291 19.8509C20.1387 19.7809 19.8744 19.6575 19.7414 19.5776C19.6045 19.4969 19.404 19.3487 19.2905 19.2482C19.177 19.1476 18.9926 18.93 18.8784 18.7651C18.7452 18.5726 18.6342 18.344 18.5649 18.1267C18.5063 17.9395 18.4435 17.6391 18.4234 17.4588C18.3933 17.1763 18.4042 17.0457 18.5014 16.4885C18.6005 15.9438 18.6378 15.7978 18.76 15.5304C18.842 15.3562 19.0217 15.0548 19.1592 14.8583C19.3006 14.6626 19.5225 14.3928 19.6523 14.2633C19.786 14.1344 20.057 13.9015 20.2584 13.7486C20.4598 13.5957 20.8681 13.327 21.1656 13.147C21.467 12.9677 21.9815 12.6817 22.3091 12.5111Z" fill="white"/>
              </g>
              <defs>
              <filter id="filter0_d_12722_4458" x="13.9727" y="4.42578" width="18.5938" height="24.1426" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feOffset dx="-0.5"/>
              <feComposite in2="hardAlpha" operator="out"/>
              <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_12722_4458"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_12722_4458" result="shape"/>
              </filter>
              </defs>
            </svg>
          </button>
          
          <button 
            className={`design-tab ${designModal.activeTab === 'backgrounds' ? 'active' : ''}`}
            onClick={() => handleTabClick('backgrounds')}
            style={{
              flex: 1,
              border: 'none',
              borderRadius: '50px',
              background: 'transparent',
              color: 'white',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M4 10.3043C4 8.47941 5.41098 7 7.15152 7H26.8485C28.589 7 30 8.47941 30 10.3043V22.6957C30 24.5206 28.589 26 26.8485 26H7.15152C5.41098 26 4 24.5206 4 22.6957V10.3043ZM24.4848 12.3696C24.4848 13.5102 23.603 14.4348 22.5152 14.4348C21.4273 14.4348 20.5455 13.5102 20.5455 12.3696C20.5455 11.229 21.4273 10.3043 22.5152 10.3043C23.603 10.3043 24.4848 11.229 24.4848 12.3696ZM8.62641 21.8696H25.3175C25.9583 21.8696 26.3311 21.1101 25.9586 20.5633L23.5157 16.9775C23.2116 16.5311 22.5846 16.5137 22.2585 16.9427L20.6475 19.0619C20.3215 19.4907 19.6948 19.4736 19.3906 19.0276L14.6652 12.1015C14.3448 11.632 13.6756 11.643 13.3695 12.1229L7.97153 20.5842C7.62136 21.1331 7.99659 21.8696 8.62641 21.8696Z" fill="white"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div 
        className="design-content flex-grow-1" 
        style={{
          overflowY: 'auto',
          maxHeight: 'calc(100% - 127px)',
          width: '344px',
          ...(designModal.activeTab === 'backgrounds' || designModal.activeTab === 'cards' ? {paddingLeft: 0, paddingTop: 4} : {})
        }}
      >
        {/* Patterns Tab - скрывается для FreeCell */}
        {designModal.activeTab === 'patterns' && !buttons.hideDesignFirstTab && (
          <div id="patterns-content" className="design-tab-content active">
            <div className="design-grid" style={{
              display: 'grid',
              gap: '17px',
              columnGap: '17px',
              justifyContent: 'start',
              paddingBottom: '20px'
            }}>
              {backs.map((pattern) => (
                <div 
                  key={pattern}
                  className={`design-item ${design.selectedPattern === pattern ? 'selected' : ''}`}
                  onClick={() => handleDesignChange('card_back', pattern)}
                  style={{
                    position: 'relative',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <ImageWithPlaceholder
                    baseSrc={`/assets/cards/back/${pattern}`}
                    className="design-image"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '8px'
                    }}
                  />
                  {design.selectedPattern === pattern && (
                    <div className="selection-indicator">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g filter="url(#filter0_d_1418_42219)">
                          <circle cx="14" cy="14" r="12" fill="#5CBD59"/>
                        </g>
                        <path d="M19.7681 8.45702C19.5191 8.20539 19.1126 8.20539 18.8636 8.45701L13.0125 14.3698C12.7635 14.6214 12.357 14.6214 12.108 14.3698L9.39029 11.6233C9.1413 11.3717 8.7348 11.3717 8.4858 11.6233L6.94284 13.1826C6.69752 13.4305 6.69752 13.8297 6.94284 14.0776L10.1222 17.2906L12.1239 19.3134C12.3729 19.5651 12.7794 19.5651 13.0284 19.3134L15.0142 17.3067L21.3269 10.9274C21.5723 10.6795 21.5723 10.2803 21.3269 10.0324L19.7681 8.45702Z" fill="white"/>
                        <defs>
                          <filter id="filter0_d_1418_42219" x="2" y="2" width="24" height="24.9672" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                            <feOffset dy="0.967219"/>
                            <feComposite in2="hardAlpha" operator="out"/>
                            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1418_42219"/>
                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1418_42219" result="shape"/>
                          </filter>
                        </defs>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cards Tab */}
        {designModal.activeTab === 'cards' && (
          <div id="cards-content" className="design-tab-content" style={{ display: 'block' }}>
            <div className="cards-grid" style={{ 
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              justifyItems: 'start',
              paddingBottom: '20px'
            }}>
              {fronts.map((frontName) => (
                <div 
                  key={frontName}
                  className="cards-row"
                  onClick={() => handleDesignChange('card_front', frontName)}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                    marginLeft: '8px',
                    marginTop: '8px'
                  }}
                >
                  <ImageWithPlaceholder
                    baseSrc={`/assets/cards/prev/${frontName}`}
                    className={`cards-preview-image ${design.selectedCardTheme === frontName ? 'selected' : ''}`}
                    style={{
                      display: 'block'
                    }}
                  />
                  {design.selectedCardTheme === frontName && (
                    <div className="selection-indicator">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g filter="url(#filter_cardfront)">
                          <circle cx="14" cy="14" r="12" fill="#5CBD59"/>
                        </g>
                        <path d="M19.7681 8.45702C19.5191 8.20539 19.1126 8.20539 18.8636 8.45701L13.0125 14.3698C12.7635 14.6214 12.357 14.6214 12.108 14.3698L9.39029 11.6233C9.1413 11.3717 8.7348 11.3717 8.4858 11.6233L6.94284 13.1826C6.69752 13.4305 6.69752 13.8297 6.94284 14.0776L10.1222 17.2906L12.1239 19.3134C12.3729 19.5651 12.7794 19.5651 13.0284 19.3134L15.0142 17.3067L21.3269 10.9274C21.5723 10.6795 21.5723 10.2803 21.3269 10.0324L19.7681 8.45702Z" fill="white"/>
                        <defs>
                          <filter id="filter_cardfront" x="2" y="2" width="24" height="24.9672" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                            <feOffset dy="0.967219"/>
                            <feComposite in2="hardAlpha" operator="out"/>
                            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_cardfront"/>
                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_cardfront" result="shape"/>
                          </filter>
                        </defs>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Backgrounds Tab */}
        {designModal.activeTab === 'backgrounds' && (
          <div id="backgrounds-content" className="design-tab-content" style={{ display: 'block' }}>
            <div className="backgrounds-grid" style={{ 
              justifyContent: 'start',
              paddingBottom: '20px'
            }}>
              {backgrounds.map((backgroundName) => (
                <div 
                  key={backgroundName}
                  className="background-item"
                  onClick={() => handleDesignChange('background', backgroundName)}
                  style={{
                    position: 'relative',
                    display: 'inline-block',
                  }}
                >
                  <ImageWithPlaceholder
                    baseSrc={`/assets/backgrounds/prev/${backgroundName}`}
                    className={`background-image ${design.selectedBackground === backgroundName ? 'selected' : ''}`}
                    style={{
                      display: 'block'
                    }}
                  />
                  {design.selectedBackground === backgroundName && (
                    <div className="selection-indicator">
                      <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g filter="url(#filter_background)">
                          <circle cx="14" cy="14" r="12" fill="#5CBD59"/>
                        </g>
                        <path d="M19.7681 8.45702C19.5191 8.20539 19.1126 8.20539 18.8636 8.45701L13.0125 14.3698C12.7635 14.6214 12.357 14.6214 12.108 14.3698L9.39029 11.6233C9.1413 11.3717 8.7348 11.3717 8.4858 11.6233L6.94284 13.1826C6.69752 13.4305 6.69752 13.8297 6.94284 14.0776L10.1222 17.2906L12.1239 19.3134C12.3729 19.5651 12.7794 19.5651 13.0284 19.3134L15.0142 17.3067L21.3269 10.9274C21.5723 10.6795 21.5723 10.2803 21.3269 10.0324L19.7681 8.45702Z" fill="white"/>
                        <defs>
                          <filter id="filter_background" x="2" y="2" width="24" height="24.9672" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                            <feOffset dy="0.967219"/>
                            <feComposite in2="hardAlpha" operator="out"/>
                            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.15 0"/>
                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_background"/>
                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_background" result="shape"/>
                          </filter>
                        </defs>
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
      
    </div>
  );
}; 